import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatIconModule } from '@angular/material/icon';
import { MatChipsModule } from '@angular/material/chips';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatDialog } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { ReservationService } from '@services/reservation.service';
import { AppStateService } from '@services/app-state.service';
import type { PuroReservation, DailyPayment, CreateWalkInRequest, CreateWalkInResponse } from '@models/reservation.interface';
import { CommonModule } from '@angular/common';
import { getBudgetInfo } from '@utils/budget-calculator';
import { GuestPackageType, ProductType, Prices, BACKEND_PRODUCT_MAP } from '@utils/prices';
import { WalkInDialogComponent, type WalkInFormData } from '@components/shared';

@Component({
  selector: 'app-guest-search',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatInputModule,
    MatButtonModule,
    MatCardModule,
    MatIconModule,
    MatChipsModule,
    MatProgressSpinnerModule
  ],
  templateUrl: './guest-search.component.html',
  styleUrls: ['./guest-search.component.scss']
})
export class GuestSearchComponent implements OnInit {
  puroId: string = ''///'MA-WOD74H959'//'94299170';///todo:: temporary hardcoded
  loading: boolean = false;
  reservation: PuroReservation | null = null;
  error: string | null = null;
  dailyBudget=0;

  // Wristband search properties
  wristbandId: string = '';
  wristbandLoading: boolean = false;
  wristbandError: string | null = null;
  isWalkIn: boolean = false;

  constructor(
    private reservationService: ReservationService,
    private appState: AppStateService,
    private router: Router,
    private dialog: MatDialog
  ) {}

  ngOnInit(): void {
    // Load existing reservation from state if available
    this.reservation = this.appState.reservation;
    this.dailyBudget = this.appState.dailyBudget;
    if (this.reservation) {
      this.puroId = this.reservation.identifier;
    }
    this.isWalkIn = this.appState.isWalkIn;
  }

  onSearch(): void {
    if (!this.puroId.trim()) {
      this.error = 'Molimo unesite šifru rezervacije.';
      return;
    }

    this.loading = true;
    this.error = null;
    this.reservation = null;

    // Reset all state when searching for a new reservation
    // This prevents data from previous reservation from persisting
    this.appState.resetAll();

    this.reservationService.getReservationByCode(this.puroId.trim()).subscribe({
      next: (data) => {
        this.reservation = data;
        this.appState.setReservation(data);
        this.loading = false;
        this.isWalkIn = this.reservation.extraInfo?.gatewayResponse == null;
        this.appState.setIsWalkIn(this.isWalkIn);
      },
      error: (err) => {
        this.error = err.message || 'Greška prilikom pretrage rezervacije.';
        this.loading = false;
      }
    });
  }

  onClear(): void {
    this.puroId = '';
    this.reservation = null;
    this.error = null;
    // Reset all app state, not just reservation
    this.appState.resetAll();
  }

  onContinue(): void {
    // Check if this is a walk-in reservation with today's booking
    const hasTodayReservation = this.allDays?.some(d => d.isToday) ?? false;
    const now = new Date();
    const transferDate = now.toISOString();
    if(this.appState.selectedDate != transferDate) {
      this.appState.setSelectedDate(transferDate);
    }
    // this.appState.setSelectedDate(this.tod)
    if (this.isWalkIn && hasTodayReservation) {
      // Skip verify step for walk-ins, go directly to credit
      this.appState.setIsValamarGuest(false); // Walk-in is non-Valamar guest
      this.appState.setCurrentStep(3);
      this.router.navigate(['/credit']);
    } else {
      // Normal flow: go to verify step
      this.appState.setCurrentStep(2);
      this.router.navigate(['/verify']);
    }
  }

  onDayClick(day: DailyPayment): void {
    if (!day.transferDone) return;

    // Store selected date and calculate budget for that date
    this.appState.setSelectedDate(day.date);

    const dayBudget = this.allDays
      .filter(d => d.date === day.date)
      .reduce((sum, d) => sum + (d.amount * (d.quantity || 0)), 0);

    this.appState.setDailyBudget(dayBudget);
    // Navigate directly to /send for completed transfers - checkin for this date is already done,
    // so /credit must stay off-limits (see StepGuard) to avoid re-submitting and duplicating gastro master cards
    this.appState.setDailyCheckinCompleted(true);
    this.appState.setCurrentStep(4);
    this.router.navigate(['/send']);
  }

  get budgetInfo() {
    if (!this.reservation) return null;
    // Default to 60% for initial display
    return getBudgetInfo(false, false, this.reservation.dailyBudget);
  }

  get canContinue(): boolean {
    if (!this.reservation) return false;
    // Only allow continue for Confirmed status
    if (this.reservation.status == "Cancell") return false;
    // Check if daily transfer for today is NOT done
    const hasTodayReservation = this.allDays?.some(d => d.isToday) ?? false;
    return !this.isTodayTransferDone && hasTodayReservation;
  }

  get isCancelledStatus(): boolean {
    return this.reservation?.status === 'Cancell';///todo:: statuse u enum
  }

  get statusClass(): string {///todo: provjeriti koristi li se ovo
    if (!this.reservation) return '';
    switch (this.reservation.status) {
      case 'Confirmed': return 'status-confirmed';
      // case 'Modificated': return 'status-modified';
      case 'Cancell': return 'status-cancelled';
      default: return 'status-other';
    }
  }

  get statusMessage(): string {
    if (!this.reservation) return '';
    switch (this.reservation.status) {
      case 'Confirmed': return 'Potvrđena rezervacija';
      // case 'Modificated': return 'Rezervacija je izmijenjena';
      case 'Cancell': return 'Rezervacija je otkazana';
      default: return `Status: ${this.reservation.status} - nije moguće nastaviti`;
    }
  }

  get isTodayTransferDone(): boolean {
    if (!this.reservation?.dailyTransfers) return false;
    const today = new Date().toISOString().split('T')[0];
    return this.reservation.dailyTransfers.some(dt => this.dateMatchesTransferDate(dt.transferDate, today));
  }

  hasDailyTransferForDate(dateString: string): boolean {
    if (!this.reservation?.dailyTransfers) return false;

    // dateString format is "yyyy-MM-dd"
    return this.reservation.dailyTransfers.some(dt => this.dateMatchesTransferDate(dt.transferDate, dateString));
  }

  // Helper to match transfer date regardless of format
  private dateMatchesTransferDate(transferDate: string | undefined, targetDate: string): boolean {
    if (!transferDate) return false;

    // Handle ISO format: "2026-05-29"
    if (transferDate.startsWith(targetDate)) return true;

    // Handle European format: "29.5.2026. 13:54:55" -> extract date part and convert
    const dateOnly = transferDate.split(' ')[0]; // "29.5.2026"
    const parts = dateOnly.split('.'); // ["29", "5", "2026"]

    if (parts.length === 3) {
      const [day, month, year] = parts;
      const isoDate = `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
      console.log(`Converting ${dateOnly} to ${isoDate}, target: ${targetDate}, match: ${isoDate === targetDate}`);
      return isoDate === targetDate;
    }

    return false;
  }

  get budgetPercentage(): string {
    return this.budgetInfo?.percentage.toString() || '0';
  }

  get budgetClass(): string {
    const percentage = this.budgetInfo?.percentage || 0;
    if (percentage >= 100) return 'budget-100';
    if (percentage >= 80) return 'budget-80';
    return 'budget-60';
  }

  get allDays(): DailyPayment[] {
    if (!this.reservation?.roomList || this.reservation.roomList.length === 0) {
      this.dailyBudget = 0;
      return [];
    }

    const normalizeDate = (date: Date): Date => {
      const normalized = new Date(date);
      normalized.setHours(0, 0, 0, 0);
      return normalized;
    };

    const formatLocalDate = (date: Date): string => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };

    const today = normalizeDate(new Date());
    const departureDate = this.reservation.endDate ? normalizeDate(new Date(this.reservation.endDate)) : today;

    const days: DailyPayment[] = [];
    let todayTotal = 0;

    // Debug: log dailyTransfers
    for (const room of this.reservation.roomList) {

      const roomStartDate = normalizeDate(new Date(room.startDate!));
      if (roomStartDate >= departureDate) {
        continue;
      }
      let isToday = roomStartDate.getTime() === today.getTime();
      if (isToday) {
        todayTotal += Number(room.price) * room.quantity;
      }
      const dateString = formatLocalDate(roomStartDate);
      const hasTransfer = this.hasDailyTransferForDate(dateString);

      days.push({
        date: dateString,
        amount: Number(room.price),
        isToday:isToday,
        quantity: room.quantity,
        roomName: room.roomName,
        transferDone: hasTransfer
      });
    }

    this.dailyBudget = todayTotal;
    this.appState.setDailyBudget(todayTotal);

    return days.sort((a, b) => a.date.localeCompare(b.date));
  }

  formatDate(dateStr: string): string {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${day}-${month}-${year}`;
  }

  // Not Opera linked: no Opera connection (may have online payment)
  get isNotOperaLinked(): boolean {
    return this.reservation?.virtualReservation == null;
  }

  // Show payment type info when reservation exists and is not connected to Opera
  get showPaymentTypeInfo(): boolean {
    return this.reservation != null && this.isNotOperaLinked;
  }

  // Payment type message based on reservation status
  get paymentTypeMessage(): string {
    if (this.isWalkIn) {
      return 'Gost nije dodan u Operu i nema online plaćanje. Naplata na licu mjesta.';
    }
    if (this.isNotOperaLinked) {
      return 'Rezervacija plaćena online, ali nije povezana s Operom. Potrebna ručna obrada.';///todo:: ili automatsko slanje na operu ili gumb za povezivanje
    }
    return 'Rezervacija povezana s Operom.';
  }

  onWristbandSearch(): void {
    if (!this.wristbandId.trim()) {
      this.wristbandError = 'Molimo unesite broj narukvice.';
      return;
    }

    this.wristbandLoading = true;
    this.wristbandError = null;

    this.reservationService.getReservationByWristband(this.wristbandId).subscribe({
      next: (results) => {
        this.wristbandLoading = false;

        if (results.length === 0) {
          this.wristbandError = 'Nema pronađenih rezervacija za ovu narukvicu.';
        } else {
          // Navigate to list component with results
          this.router.navigate(['/search-results'], { state: { reservations: results } });
        }
      },
      error: (err) => {
        this.wristbandError = err.message || 'Greška prilikom pretrage.';
        this.wristbandLoading = false;
      }
    });
  }

  parseDate(date?: string) {
    return this.appState.parseDate(date);
  }

  onOpenWalkInDialog(): void {
    // Format: backend labels as both value and display
    const productTypes = Object.entries(BACKEND_PRODUCT_MAP).map(([backendLabel, enumValue]) => {
      // Format the backend label to proper capitalization
      const formattedLabel = backendLabel
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ');

      return {
        value: formattedLabel,
        label: formattedLabel
      };
    });

    const packageTypes = Object.entries(GuestPackageType).map(([key, value]) => ({
      value: value,
      label: this.formatPackageLabel(value)
    }));

    const dialogRef = this.dialog.open(WalkInDialogComponent, {
      width: '600px',
      data: { productTypes, packageTypes }
    });

    dialogRef.afterClosed().subscribe((result?: WalkInFormData) => {
      if (result) {
        this.onCreateWalkIn(result);
      }
    });
  }

  async onCreateWalkIn(formData: WalkInFormData): Promise<void> {
    // Izračunaj cijene za svaku stavku zasebno
    const items = formData.chairAssignments.map(assignment => {
      // Map formatted label (e.g., "VIP Pool Area Sunbed") to ProductType enum for price calculation
      const labelKey = assignment.productType.toLowerCase();
      const productType = BACKEND_PRODUCT_MAP[labelKey] || assignment.productType as ProductType;

      // Use the price from form if provided and > 0, otherwise calculate default price
      let productPrice = assignment.productPrice ?? 0;
      if (productPrice <= 0) {
        productPrice = Prices.getLoungerPrice(productType, formData.packageType);
      }

      const daysCount = this.calculateDaysBetween(assignment.startDate, assignment.endDate);
      const totalPrice = productPrice * assignment.quantity * daysCount;

      return {
        productType: assignment.productType, // Send formatted label to backend
        quantity: assignment.quantity,
        startDate: this.formatDateForAPI(assignment.startDate),
        endDate: this.formatDateForAPI(assignment.endDate),
        productPrice,
        totalPrice
      };
    });

    const request: CreateWalkInRequest = {
      firstName: formData.firstName,
      lastName: formData.lastName,
      email: formData.email,
      packageType: formData.packageType,
      items
    };

    this.loading = true;
    this.error = null;

    this.reservationService.createWalkInReservation(request).subscribe({
      next: (response: CreateWalkInResponse) => {
        this.loading = false;

        if (response.success && response.identifier) {
          // Populate the search field and trigger search
          this.puroId = response.identifier;
          this.onSearch();

          // Set isWalkIn in app state
          this.appState.setIsWalkIn(true);
        } else {
          this.error = response.message || 'Greška prilikom kreiranja rezervacije.';
        }
      },
      error: (err) => {
        this.loading = false;
        this.error = err.message || 'Greška prilikom kreiranja walk-in rezervacije.';
      }
    });
  }

  calculateDaysBetween(startDate: Date, endDate: Date): number {
    if (!startDate || !endDate) return 0;
    const start = new Date(startDate);
    const end = new Date(endDate);
    const diffTime = Math.abs(end.getTime() - start.getTime());
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  }

  formatProductLabel(value: string): string {
    const labels: Record<string, string> = {
      [ProductType.VIP_SUNBED]: 'VIP Pool Area Sunbed',
      [ProductType.VIP_BALINESE]: 'VIP Balinese',
      [ProductType.POOL_SUNBED]: 'Pool Sunbed',
      [ProductType.OASIS_SUNBED]: 'Oasis Sunbed',
      [ProductType.OASIS_BALINESE]: 'Oasis Balinese',
      [ProductType.PALM_SUNBED]: 'Palm Sunbed',
      [ProductType.PALM_BALINESE]: 'Palm Balinese',
      [ProductType.BEACH_SUNBED]: 'Beach Sunbed',
      [ProductType.BEACH_BALINESE]: 'Beach Balinese'
    };
    return labels[value] || value;
  }

  formatPackageLabel(value: string): string {
    const labels: Record<string, string> = {
      [GuestPackageType.VLEVEL]: 'V-Level (100% kredit)',
      [GuestPackageType.COLLECTION]: 'Collection (60% kredit)',
      [GuestPackageType.EXTERNAL]: 'External (40% kredit)'
    };
    return labels[value] || value;
  }

  formatDateForAPI(date: Date): string {
    if (!date) return '';
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }
}
